import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Card, Spinner } from '@/components/ui';
import { EmptyState } from '@/components/feedback/EmptyState';
import { ErrorState } from '@/components/feedback/ErrorState';
import { SkeletonList } from '@/components/feedback/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { formatDate } from '@/lib/utils/date';
import { routePaths } from '@/app/routes';
import type { NomenclatureResolver } from '@/lib/nomenclature/resolver';
import { useActivePatient } from '@/features/patients/hooks/useActivePatient';
import { useExitOnPatientSwitch } from '@/features/patients/hooks/useExitOnPatientSwitch';
import { useResolver } from '@/features/nomenclature/hooks/useResolver';
import { useResults } from '../hooks/useResults';
import { useOpenDocument } from '../hooks/useOpenDocument';
import { categoryForConsType, showsInProgress } from '../lib/categories';
import type { MedicalResult, ResultDocument } from '../types/result.types';

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ResultDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { consId = '' } = useParams();
  const { patient } = useActivePatient();
  const { data: results = [], isLoading, isError, refetch } = useResults(patient?.fhirId ?? null);
  const { data: resolver = null } = useResolver();
  const open = useOpenDocument();

  // A result belongs to one patient: switching the active patient while this
  // page is open returns to the list instead of showing "not found".
  useExitOnPatientSwitch(routePaths.results);

  const result = results.find((r) => r.consId === decodeURIComponent(consId));

  const onOpen = (doc: ResultDocument) => {
    if (!patient || !result) return;
    // Open the tab synchronously (within the click gesture) so popup blockers
    // allow it; point it at the blob URL once the PDF is fetched.
    const win = window.open('', '_blank');
    open.mutate(
      { patientFhirId: patient.fhirId, result, doc },
      {
        onSuccess: ({ objectUrl, name }) => {
          if (win && !win.closed) {
            win.location.href = objectUrl;
          } else {
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        },
        onError: () => win?.close(),
      },
    );
  };

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate(routePaths.results)}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t('results.back_to_list')}
      </button>

      {isLoading ? (
        <SkeletonList rows={3} />
      ) : isError ? (
        <ErrorState description={t('results.load_error')} onRetry={() => void refetch()} />
      ) : !result ? (
        <EmptyState title={t('results.not_found')} />
      ) : (
        <DetailBody result={result} resolver={resolver} open={open} onOpen={onOpen} />
      )}
    </div>
  );
}

interface DetailBodyProps {
  result: MedicalResult;
  resolver: NomenclatureResolver | null;
  open: ReturnType<typeof useOpenDocument>;
  onOpen: (doc: ResultDocument) => void;
}

function DetailBody({ result, resolver, open, onOpen }: DetailBodyProps) {
  const { t } = useTranslation();
  const category = categoryForConsType(result.consType);

  const physicianName = resolver?.physicianName(result.physicianId) ?? null;
  const physicianDegree = resolver?.physicianDegree(result.physicianId) ?? null;
  const specialty = resolver?.specialtyName(result.specialityId) ?? null;
  const location = resolver?.location(result.locationId) ?? null;

  const downloadable = result.documents.filter((d) => d.canDownload);
  const activeDocId = open.isPending ? open.variables?.doc.docId : undefined;

  const rows: Array<[string, string | null]> = [
    [t('results.detail_specialty'), specialty],
    [t('results.detail_date'), formatDate(result.date)],
    [t('results.detail_location'), location?.name ?? null],
    [t('results.detail_address'), location?.address ?? null],
  ];

  return (
    <>
      <PageHeader
        title={result.consDesc || result.typeDesc || t('results.untitled')}
        subtitle={category.name}
        actions={
          result.isFinal || showsInProgress(result.consType) ? (
            <Badge tone={result.isFinal ? 'success' : 'warning'}>
              {result.isFinal ? t('results.final') : t('results.in_progress')}
            </Badge>
          ) : undefined
        }
      />

      <Card className="flex flex-col gap-4 p-5">
        {physicianName && (
          <div className="flex items-center gap-3 border-b border-muted-200 pb-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold text-ink">
                {physicianDegree ? `${physicianDegree} ${physicianName}` : physicianName}
              </p>
              {specialty && <p className="truncate text-sm text-ink-mute">{specialty}</p>}
            </div>
          </div>
        )}

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {rows
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-ink-mute">{label}</dt>
                <dd className="text-right font-semibold text-ink">{value}</dd>
              </div>
            ))}
        </dl>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <h2 className="text-base font-bold text-ink">{t('results.documents_title')}</h2>
        {result.docCount === 0 ? (
          <p className="text-sm text-ink-mute">{t('results.no_documents')}</p>
        ) : downloadable.length === 0 ? (
          <p className="text-sm text-ink-mute">
            {t('results.documents_summary', { available: result.docCountAvail, total: result.docCount })}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {downloadable.map((doc, index) => {
              const busy = activeDocId === doc.docId;
              return (
                <button
                  key={doc.docId}
                  type="button"
                  disabled={open.isPending}
                  onClick={() => onOpen(doc)}
                  className="inline-flex items-center justify-between gap-2 rounded-input bg-primary-light px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/15 disabled:opacity-60"
                >
                  <span>{downloadable.length > 1 ? t('results.open_n', { n: index + 1 }) : t('results.open')}</span>
                  {busy ? <Spinner size={16} /> : <DownloadIcon />}
                </button>
              );
            })}
          </div>
        )}
        {open.isError && <p className="text-sm text-danger">{t('results.download_error')}</p>}
      </Card>
    </>
  );
}
